'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { FieldError, SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useUser } from '@/contexts/UserContext'
import type { SignUpFormData } from '@/lib/types/index'
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Separator } from "@/components/ui/separator"
import { Background } from '@/components/home/DarkstarBackground'
import FlowerOfLife from '@/components/Flower'
import { Mesh } from '@/components/background/Mesh';

const schema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().min(8, 'Password must be at least 8 characters').required('Password is required'),
}).required()

export default function SignUp() {
  const { signUpWithEmail, signInWithGoogle, userProfile, loading, error: contextError } = useUser()
  const [error, setError] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const router = useRouter()
  
  const { 
    register, 
    handleSubmit, 
    formState: { errors } 
  } = useForm<SignUpFormData>({
    resolver: yupResolver(schema)
  })

  const onSubmit: SubmitHandler<SignUpFormData> = async (data) => {
    setError('')
    setIsSubmitting(true)
    try {
      await signUpWithEmail(data.email, data.password)
      router.push('/select-handle')
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to create account')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setError('')
    setIsSubmitting(true)
    try {
      const hasProfile = await signInWithGoogle()
      if (!hasProfile) {
        router.push('/select-handle')
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to sign in with Google')
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    let mounted = true
    
    if (mounted && userProfile && !loading && !isSubmitting) {
      router.push(`/${userProfile.handle}/profile`)
    }

    return () => {
      mounted = false
    }
  }, [userProfile, loading, router, isSubmitting])

  if (loading) {
    return (
      <>
        <Background />
        <div className="flex items-center justify-center min-h-screen">
          <div className="rounded-full h-64 w-64">
            <FlowerOfLife color="white" maxSize={256} speed="2s" />
          </div>
        </div>
      </>
    )
  }

  const getFieldError = (error: FieldError | undefined): string => {
    return error?.message || '';
  }

  return (
    <>
      <Mesh />
      <div className="relative min-h-screen flex items-center justify-center overflow-auto">
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className=" bg-white/50 backdrop-blur-sm rounded-3xl border border-white/0 w-[350px] sm:w-[500px] shadow-xl">
            <CardHeader>
              <CardTitle className="text-primary text-lg">Create your account</CardTitle>
              <CardDescription className="text-primary/70 text-xs leading-tight">You&rsquo;re just moments away from access &mdash; create your account now to save your readings and revisit your spiritual journey anytime.</CardDescription>
            </CardHeader>
            <CardContent>
            <Button 
                variant="default"
                size="xl"
                className="w-full mt-4 bg-white/80 hover:bg-white/100 text-black rounded-xl" 
                onClick={handleGoogleSignIn}
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_13183_10121)"><path d="M20.3081 10.2303C20.3081 9.55056 20.253 8.86711 20.1354 8.19836H10.7031V12.0492H16.1046C15.8804 13.2911 15.1602 14.3898 14.1057 15.0879V17.5866H17.3282C19.2205 15.8449 20.3081 13.2728 20.3081 10.2303Z" fill="#3F83F8"/><path d="M10.7019 20.0006C13.3989 20.0006 15.6734 19.1151 17.3306 17.5865L14.1081 15.0879C13.2115 15.6979 12.0541 16.0433 10.7056 16.0433C8.09669 16.0433 5.88468 14.2832 5.091 11.9169H1.76562V14.4927C3.46322 17.8695 6.92087 20.0006 10.7019 20.0006V20.0006Z" fill="#34A853"/><path d="M5.08857 11.9169C4.66969 10.6749 4.66969 9.33008 5.08857 8.08811V5.51233H1.76688C0.348541 8.33798 0.348541 11.667 1.76688 14.4927L5.08857 11.9169V11.9169Z" fill="#FBBC04"/><path d="M10.7019 3.95805C12.1276 3.936 13.5055 4.47247 14.538 5.45722L17.393 2.60218C15.5852 0.904587 13.1858 -0.0287217 10.7019 0.000673888C6.92087 0.000673888 3.46322 2.13185 1.76562 5.51234L5.08732 8.08813C5.87733 5.71811 8.09302 3.95805 10.7019 3.95805V3.95805Z" fill="#EA4335"/></g><defs><clipPath id="clip0_13183_10121"><rect width="20" height="20" fill="white" transform="translate(0.5)"/></clipPath></defs>
                </svg>
                Sign up with Google
              </Button>
              
              <div className="mt-6">
                <Separator className="bg-white/20" />
                <p className="text-center text-sm text-primary/70 mt-2">Or continue with</p>
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="grid w-full items-center gap-4">
                  <div className="flex flex-col space-y-1.5">
                    <Label htmlFor="email" className="text-primary">Email</Label>
                    <Input 
                      id="email" 
                      type="email" 
                      placeholder="Your email" 
                      {...register('email')}
                      className="bg-white/20 border-white/20 text-primary placeholder:text-primary/50 rounded-xl"
                    />
                    {errors.email && (
                      <span className="text-sm text-red-400">
                        {getFieldError(errors.email)}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col space-y-1.5 pb-4">
                    <Label htmlFor="password" className="text-primary">Password</Label>
                    <Input 
                      id="password" 
                      type="password" 
                      placeholder="Your password" 
                      {...register('password')}
                      className="bg-white/20 border-white/20 text-primary placeholder:text-primary/50 rounded-xl"
                    />
                    {errors.password && (
                      <span className="text-sm text-red-400">
                        {getFieldError(errors.password)}
                      </span>
                    )}
                  </div>
                </div>
                {(error || contextError) && (
                  <Alert className="mt-4 bg-red-500/20 text-primary border-red-500/50">
                    <AlertDescription>
                      {error || (contextError instanceof Error ? contextError.message : '')}
                    </AlertDescription>
                  </Alert>
                )}
                <Button 
                  className="w-full mt-6 bg-white/20 hover:bg-white/30 text-primary rounded-xl" 
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating account...' : 'Sign up'}
                </Button>
              </form>
            </CardContent>
            <CardFooter className="flex flex-col justify-center space-y-2">
              <Link href="/signin" className="text-sm text-primary hover:text-primary/80">
                Already have an account? Sign in
              </Link>
              <div className="flex items-center justify-between gap-2">
                <Link href="/tos" className="text-sm text-primary hover:text-primary/80">
                  Terms of Service
                </Link>
                <Link href="/privacy" className="text-sm text-primary hover:text-primary/80">
                  Privacy Policy
                </Link>
              </div>
            </CardFooter>
          </Card>
        </motion.div>
      </div>
    </>
  )
}